import React, {useCallback, useContext, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {FabricContext} from '~/common/fabric/context/FabricContext';
import {useEnvelope} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Envelope/actions/useEnvelope';
import {StickersCanvas} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Stamp/StickersCanvas';
import {selectIsPremium} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {useNavigator} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/NavBottom/hooks/useNavigator';
import {Sections} from '~/CreateFabricInvitation/routes/EventCreateDesign/constants';
import {selectLoaded} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {selectIsProOrPartyPass} from '~/common/fabric/User/reducers/user';
import linerShading from '~/../images/liner_shading.png';

import './Envelope.sass';

export const Envelope = ({
  id,
  type,
  onLoad,
  redirectTo,
  message,
  className,
  selectedLiner,
  selectedStamp,
  style = {},
  canvas = false,
  parentNode = '',
  forceShowEnvelope = false,
}) => {
  const ref = useRef();
  const isPremium = useSelector(selectIsPremium);
  const {selectedEnvelope} = useEnvelope();
  const isLargishAndUp = useIsLargishAndUp();
  const loaded = useSelector(selectLoaded);
  const isProOrPartyPass = useSelector(selectIsProOrPartyPass);
  const isMobile = !isLargishAndUp;
  const navigate = useNavigator({
    isMobile,
    disableAutoOpenEditor: redirectTo === Sections.CARD_DESIGN,
  });

  const {inIsolatedEditMode} = useContext(FabricContext);

  const setCurrentDimensions = useCallback(() => {
    const envelopeSizer = document.getElementById(parentNode);

    if (isMobile && envelopeSizer) {
      ref.current.style.height = `${envelopeSizer.offsetWidth}px`;
      ref.current.style.width = `${envelopeSizer.offsetHeight}px`;
    }
  }, [isMobile, parentNode]);

  useEffect(() => {
    setCurrentDimensions();
  }, [inIsolatedEditMode, setCurrentDimensions]);

  useEffect(() => {
    if (ref.current) {
      window.addEventListener('resize', setCurrentDimensions);
      return () => {
        window.removeEventListener('resize', setCurrentDimensions);
      };
    }
    return undefined;
  }, [setCurrentDimensions]);

  return (
    (isPremium || isProOrPartyPass || forceShowEnvelope) && (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        ref={ref}
        onClick={loaded && redirectTo ? () => navigate(redirectTo) : undefined}
        className={`envelope_image__wrapper envelope_image__wrapper__${type} ${
          redirectTo && 'envelope_image__wrapper--clickable'
        } ${!loaded ? 'envelope_image__wrapper--clickable--disabled' : ''} ${className}`}
        style={style}
      >
        {selectedEnvelope && (
          <img
            src={selectedEnvelope[type]}
            className="envelope_image"
            id={id}
            onLoad={onLoad}
            alt=""
          />
        )}
        {selectedLiner && (
          <>
            <img alt="" src={linerShading} className="envelope_image__wrapper__liner__shadow" />
            <img src={selectedLiner} className="envelope_image__wrapper__liner" alt="" />
          </>
        )}
        {selectedStamp && (
          <img className="envelope_image__wrapper__stamp" src={selectedStamp} alt="" />
        )}
        {message && (
          <div className="envelope_image__wrapper__text">
            <span style={{color: selectedEnvelope?.text_color}}>{message}</span>
          </div>
        )}
        {canvas && <StickersCanvas className="envelope_image__wrapper__canvas" />}
      </div>
    )
  );
};
